import { Box, Card, CardContent, Typography, Chip, Stack, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

interface Item {
    item: string;
    shippedQuantity: number;
    totalPrice: number;
    unitPrice: number;
}

interface InvoiceData {
    driverName: string;
    invoiceDate: string;
    invoiceNo: number;
    items: Item[];
    status?: string;
}

interface BasicCardProps {
    data: InvoiceData;
    onSelect?: (data: InvoiceData) => void;
    isSelected?: boolean;
    isAnySelected?: boolean;
}

const GREEN = '#2e7d32';
const GREEN_LIGHT = '#4caf50';
const GREEN_BG = alpha('#4caf50', 0.07);

export default function InvoiceCard({ data, onSelect, isSelected, isAnySelected }: BasicCardProps) {
    const isCaptured = data.status === 'sale_captured';
    const isAccept = data.status === 'accept';

    const handleClick = () => {
        if (onSelect) onSelect(data);
    };

    // Border colour: teal when selected, status-based otherwise
    const borderColor = isSelected
        ? GREEN_LIGHT
        : isCaptured
        ? alpha('#9e9e9e', 0.4)
        : alpha('#4caf50', 0.3);

    return (
        <Card
            onClick={handleClick}
            elevation={0}
            sx={{
                cursor: 'pointer',
                borderRadius: 2,
                border: `1.5px solid ${borderColor}`,
                borderLeft: isSelected
                    ? `4px solid ${GREEN_LIGHT}`
                    : isCaptured
                    ? `4px solid #bdbdbd`
                    : `4px solid ${alpha(GREEN_LIGHT, 0.5)}`,
                boxShadow: isSelected ? `0 4px 20px ${alpha(GREEN_LIGHT, 0.22)}` : 'none',
                opacity: isAnySelected && !isSelected ? 0.5 : 1,
                transition: 'all 0.2s ease',
                bgcolor: isSelected ? GREEN_BG : isCaptured ? '#fafafa' : '#fff',
                '&:hover': {
                    opacity: 1,
                    borderColor: isCaptured ? '#bdbdbd' : GREEN_LIGHT,
                    boxShadow: isCaptured ? 'none' : `0 4px 14px ${alpha(GREEN_LIGHT, 0.18)}`,
                },
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {/* Selected checkmark badge */}
            {isSelected && (
                <Box
                    sx={{
                        position: 'absolute', top: 8, right: 8,
                        width: 22, height: 22, borderRadius: '50%',
                        bgcolor: GREEN_LIGHT,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <TaskAltIcon sx={{ fontSize: 14, color: '#fff' }} />
                </Box>
            )}

            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>

                {/* ── Header ── */}
                <Stack direction="row" alignItems="flex-start" spacing={1} mb={1.25}>
                    <Box
                        sx={{
                            width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                            bgcolor: isSelected ? alpha(GREEN_LIGHT, 0.15) : isCaptured ? 'grey.100' : alpha(GREEN_LIGHT, 0.08),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        {isCaptured
                            ? <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            : <ReceiptLongIcon sx={{ fontSize: 16, color: isSelected ? GREEN : alpha(GREEN, 0.6) }} />
                        }
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={800} color={isSelected ? GREEN : isCaptured ? 'text.disabled' : 'text.primary'} sx={{ lineHeight: 1.1, fontSize: '1rem' }}>
                            #{data.invoiceNo}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                            {new Date(data.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </Typography>
                    </Box>
                </Stack>

                {/* ── Status + Driver ── */}
                <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25} flexWrap="wrap" useFlexGap>
                    {isCaptured ? (
                        <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
                            label="Sale Captured"
                            size="small"
                            sx={{
                                height: 20, fontSize: '0.6rem', fontWeight: 700,
                                bgcolor: alpha('#9e9e9e', 0.12), color: 'text.secondary',
                                '& .MuiChip-icon': { color: 'text.disabled' },
                            }}
                        />
                    ) : (
                        <Chip
                            label="Ready"
                            size="small"
                            sx={{
                                height: 20, fontSize: '0.6rem', fontWeight: 700,
                                bgcolor: alpha(GREEN_LIGHT, 0.12), color: GREEN,
                            }}
                        />
                    )}
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <LocalShippingIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
                            {data.driverName}
                        </Typography>
                    </Stack>
                </Stack>

                <Divider sx={{ mb: 1.25 }} />

                {/* ── Items ── */}
                <Stack spacing={0.5}>
                    {data.items.map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1, py: 0.6,
                                borderRadius: 1.25,
                                bgcolor: isCaptured ? 'grey.50' : isSelected ? alpha(GREEN_LIGHT, 0.06) : alpha(GREEN_LIGHT, 0.04),
                            }}
                        >
                            <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
                                <LocalGasStationIcon sx={{ fontSize: 11, color: isCaptured ? 'text.disabled' : GREEN_LIGHT, flexShrink: 0 }} />
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color={isCaptured ? 'text.disabled' : 'text.primary'}
                                    noWrap
                                    sx={{ maxWidth: 110 }}
                                >
                                    {item.item}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Chip
                                    label={`×${item.shippedQuantity}`}
                                    size="small"
                                    sx={{
                                        height: 17, fontSize: '0.58rem', fontWeight: 700,
                                        bgcolor: isCaptured ? 'grey.100' : alpha(GREEN_LIGHT, 0.1),
                                        color: isCaptured ? 'text.disabled' : GREEN,
                                    }}
                                />
                                <Typography variant="caption" fontWeight={700} color={isCaptured ? 'text.disabled' : GREEN} sx={{ minWidth: 40, textAlign: 'right' }}>
                                    ₹{item.totalPrice}
                                </Typography>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
}
